import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useNespresso } from "../context/NespressoContext";
import { CheckCircle } from "lucide-react";

// Nespresso brand colors
const NESPRESSO = {
  brown: "#986f38",
  brownHover: "#815e2f",
  gold: "#c69f36",
  black: "#000000",
  darkGray: "#292929",
  lightGray: "#f9f9f9",
  border: "#e6e5e4",
};

// Demo user name - in production this would come from the scanned loyalty card
const DEMO_USER_NAME = "Jamie";

// Checklist items per spec
const CHECKLIST_ITEMS = [
  "Ensure your bag contains coffee pods only",
  "Ensure your bag is wiped clean and sealed",
];

const NespressoTakebackWelcomeView: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch, currentTier } = useNespresso();
  const [showChecklist, setShowChecklist] = useState(true);
  const [checkedItems, setCheckedItems] = useState<number[]>([]);

  // Reset gamification state when entering the takeback flow
  useEffect(() => {
    dispatch({ type: "RESET_GAMIFICATION" });
  }, [dispatch]);

  // Auto-advance checklist items then transition to main content
  useEffect(() => {
    if (!showChecklist) return;

    const timers: NodeJS.Timeout[] = [];

    // Check items one by one
    CHECKLIST_ITEMS.forEach((_, index) => {
      timers.push(
        setTimeout(() => {
          setCheckedItems((prev) => [...prev, index]);
        }, 800 + index * 600)
      );
    });

    // Transition to main welcome after checklist completes
    timers.push(
      setTimeout(() => {
        setShowChecklist(false);
      }, 800 + CHECKLIST_ITEMS.length * 600 + 800)
    );

    return () => timers.forEach((t) => clearTimeout(t));
  }, [showChecklist]);

  const handlePlaceBag = () => {
    navigate("/nespresso/scan");
  };

  const handleBack = () => {
    navigate("/nespresso");
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: NESPRESSO.black }}
    >
      {/* Header */}
      <header className="py-4 px-8 flex items-center justify-between border-b border-white/10">
        <button
          onClick={handleBack}
          className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <span
          className="font-medium uppercase tracking-widest text-sm"
          style={{ color: NESPRESSO.gold }}
        >
          Nespresso Recycling Program
        </span>
        <div className="w-16" />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8">
        <AnimatePresence mode="wait">
          {showChecklist ? (
            /* Step 1C: Pre-return Checklist (automated, no interaction required) */
            <motion.div
              key="checklist"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-md"
            >
              <h2 className="text-3xl font-light text-white mb-8">
                Before you begin...
              </h2>
              <div className="space-y-4">
                {CHECKLIST_ITEMS.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0.5 }}
                    animate={{
                      opacity: checkedItems.includes(index) ? 1 : 0.5,
                    }}
                    className="flex items-center gap-4 text-left"
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300"
                      style={{
                        backgroundColor: checkedItems.includes(index)
                          ? NESPRESSO.gold
                          : "transparent",
                        border: `2px solid ${
                          checkedItems.includes(index)
                            ? NESPRESSO.gold
                            : "rgba(255,255,255,0.3)"
                        }`,
                      }}
                    >
                      {checkedItems.includes(index) && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        >
                          <CheckCircle className="w-5 h-5 text-black" />
                        </motion.div>
                      )}
                    </div>
                    <span
                      className={`text-lg font-light transition-colors duration-300 ${
                        checkedItems.includes(index)
                          ? "text-white"
                          : "text-white/50"
                      }`}
                    >
                      {item}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ) : (
            /* Step 2: Welcome + Place Bag Instruction */
            <motion.div
              key="welcome"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-lg"
            >
              {/* Welcome greeting */}
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="text-5xl font-light text-white mb-4"
              >
                Welcome, {DEMO_USER_NAME}
              </motion.h1>

              {/* Tier badge */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="flex items-center justify-center gap-2 mb-8"
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: currentTier.badge.color }}
                />
                <span className="text-white/60 text-sm">
                  {currentTier.name} • {currentTier.descriptor}
                </span>
              </motion.div>

              {/* Tray icon */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="mb-8"
              >
                <div
                  className="w-32 h-32 rounded-full flex items-center justify-center mx-auto"
                  style={{ backgroundColor: `${NESPRESSO.brown}30` }}
                >
                  <div
                    className="w-24 h-24 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${NESPRESSO.brown}50` }}
                  >
                    <svg
                      className="w-12 h-12"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ color: NESPRESSO.gold }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                      />
                    </svg>
                  </div>
                </div>
              </motion.div>

              {/* Instruction */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
                className="text-2xl text-white/80 font-light mb-10"
              >
                Place your first bag on the tray
              </motion.p>

              {/* Demo button - in real use this would be triggered by weight sensor */}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}
                onClick={handlePlaceBag}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="text-white px-10 py-4 font-medium text-lg transition-colors"
                style={{
                  backgroundColor: NESPRESSO.brown,
                  borderRadius: "3px",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = NESPRESSO.brownHover)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = NESPRESSO.brown)
                }
              >
                Simulate Bag Placement
              </motion.button>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.7 }}
                className="text-white/40 text-sm mt-4"
              >
                Weight sensor will detect your bag automatically
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="py-4 px-8 text-center text-white/30 text-xs border-t border-white/5 font-light">
        Powered by Pentatonic AI
      </footer>
    </div>
  );
};

export default NespressoTakebackWelcomeView;
